import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import QRCodeStyling, {
  Options as QRCodeStylingOptions,
} from "qr-code-styling";
import { useTranslation } from "react-i18next";
import { backButton } from "@telegram-apps/sdk-react";
import { ChevronUp } from "lucide-react";
import IconQrCode from "@/assets/icons/qr-code.svg?react";
import IconClose from "@/assets/icons/close.svg?react";
import VibratingButton from "@/components/base/button/VibratingButton.tsx";
import LoyaltyScanner from "./LoyaltyScanner.tsx";
import { useGenerateQR } from "../api";
import { useGetProfile } from "@/pages/profile/api";

const qrOptions: QRCodeStylingOptions = {
  width: 212,
  height: 212,
  margin: 4,
  type: "svg",
  image: "images/don-logo.svg",
  qrOptions: { typeNumber: 0, mode: "Byte", errorCorrectionLevel: "Q" },
  imageOptions: { imageSize: 0.5, margin: 4, crossOrigin: "anonymous" },
  dotsOptions: { type: "extra-rounded", color: "#000000", roundSize: true },
  backgroundOptions: { round: 0, color: "#ffffff" },
  cornersSquareOptions: { type: "extra-rounded", color: "#000000" },
};

const QR_REFRESH_SECONDS = 30;

// Fixed sheet geometry — peek shows the top bar; swipe up reveals the QR.
// Peek shows the handle + label AND a sliver of the QR card so it's clear
// there's a QR to pull up.
const PEEK = 150;
// Non-QR content height (peek bar + countdown + hint + margins). The
// expanded sheet is sized to QR + this, so a big QR fills it with no gap.
const SHEET_EXTRA = 216;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onScanned: (text: string) => void;
}

/** Full-screen QR experience: live camera fills the screen (scan), and a
 *  bottom sheet peeks at the bottom — swipe it up to reveal the user's own
 *  QR code, swipe down to keep scanning. */
export default function LoyaltyScanScreen({ isOpen, onClose, onScanned }: Props) {
  const { t } = useTranslation();
  const { data: profile } = useGetProfile();

  const [expanded, setExpanded] = useState(false);
  const [qrSize, setQrSize] = useState(240);
  const [sheetH, setSheetH] = useState(240 + SHEET_EXTRA);
  const [qrReady, setQrReady] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(QR_REFRESH_SECONDS);

  // QR fills the phone width (capped); the sheet is sized to the QR so the
  // big code fills the panel with no empty gap below.
  useEffect(() => {
    if (!isOpen) return;
    // Compact QR, capped so it never gets huge on a wide
    // (desktop/notebook) viewport.
    const qs = Math.min(window.innerWidth - 48, 240);
    setQrSize(qs);
    setSheetH(qs + SHEET_EXTRA);
  }, [isOpen]);
  const maxY = sheetH - PEEK;

  const qrSizeRef = useRef(qrSize);
  qrSizeRef.current = qrSize;

  // Re-render the QR at the current size once it's ready / on resize.
  useEffect(() => {
    if (qrReady) qrCode.update({ width: qrSize, height: qrSize });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qrSize]);
  const prevSecondsRef = useRef(QR_REFRESH_SECONDS);
  const justReset = secondsLeft > prevSecondsRef.current;
  useEffect(() => {
    prevSecondsRef.current = secondsLeft;
  });

  const generateQR = useGenerateQR();
  const qrCode = useMemo(() => new QRCodeStyling(qrOptions), []);
  const qrRef = useRef<HTMLDivElement>(null);

  // Hide Telegram's native back button while the full-screen scan is open
  // so it doesn't sit under our close (X). Our X closes the overlay; the
  // back button is restored on close.
  useEffect(() => {
    if (!isOpen || !backButton.isMounted()) return;
    backButton.hide();
    return () => {
      if (backButton.isMounted()) backButton.show();
    };
  }, [isOpen]);

  // Generate the QR as soon as the screen opens so a sliver of it is
  // already visible in the peeking sheet. Reset on close.
  useEffect(() => {
    if (!isOpen) {
      setExpanded(false);
      setQrReady(false);
      return;
    }
    const size = qrSizeRef.current;
    generateQR
      .mutateAsync()
      .then((result) => {
        qrCode.update({ data: result.qr_data, width: size, height: size });
        setQrReady(true);
      })
      .catch(() => {
        qrCode.update({
          data: `don://loyalty/${profile?.id || "fallback"}`,
          width: size,
          height: size,
        });
        setQrReady(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Mount the QR canvas only once the data is set (avoids an empty flash).
  useEffect(() => {
    if (qrReady && qrRef.current) {
      qrRef.current.innerHTML = "";
      qrCode.append(qrRef.current);
    }
  }, [qrReady, qrCode]);

  // Countdown + silent auto-refresh while the screen is open.
  useEffect(() => {
    if (!isOpen || !qrReady) return;
    setSecondsLeft(QR_REFRESH_SECONDS);
    const tick = setInterval(
      () => setSecondsLeft((s) => (s > 0 ? s - 1 : 0)),
      1000
    );
    const refresh = setInterval(() => {
      generateQR
        .mutateAsync()
        .then((result) => {
          qrCode.update({
            data: result.qr_data,
            width: qrSizeRef.current,
            height: qrSizeRef.current,
          });
          setSecondsLeft(QR_REFRESH_SECONDS);
        })
        .catch(() => {});
    }, QR_REFRESH_SECONDS * 1000);
    return () => {
      clearInterval(tick);
      clearInterval(refresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, qrReady, qrCode]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[70] bg-black overflow-hidden">
      {/* Full-screen camera — kept alive the whole time the screen is open
          (permission asked once); decoding pauses while the QR is shown. */}
      <LoyaltyScanner
        isOpen={isOpen}
        paused={expanded}
        onScanned={onScanned}
        className="absolute inset-0 w-full h-full bg-black"
      />

      {/* Dim scrim behind the QR sheet — turns the camera into a soft,
          intentional backdrop (no stark black gap) and keeps focus on the QR. */}
      <motion.div
        className="absolute inset-0 z-10 bg-black/90 backdrop-blur-xl"
        initial={false}
        animate={{ opacity: expanded ? 1 : 0 }}
        transition={{ duration: 0.25 }}
        style={{ pointerEvents: expanded ? "auto" : "none" }}
        onClick={() => setExpanded(false)}
      />

      {/* Top bar — close */}
      <div
        className="absolute top-0 inset-x-0 z-20 flex items-center justify-between px-4"
        style={{ paddingTop: "calc(env(safe-area-inset-top) + 0.75rem)" }}
      >
        <VibratingButton
          onClick={onClose}
          vibrationType="light"
          aria-label={t("close")}
          className="size-10 rounded-full bg-dark/60 backdrop-blur flex-center text-white active:bg-dark/80"
        >
          <IconClose className="size-6" />
        </VibratingButton>
        <span className="text-sm font-semibold text-white/90">
          {t("scan", "Skanlash")}
        </span>
        <span className="size-10" />
      </div>

      {/* Scan hint (only while collapsed) */}
      {!expanded && (
        <div className="absolute inset-x-0 top-1/2 mt-[150px] z-10 px-8 text-center pointer-events-none">
          <p className="text-xs text-white/70 leading-[160%]">
            {t("scan_instructions", "QR kodni ramka ichiga to'g'rilang")}
          </p>
        </div>
      )}

      {/* Swipe-up QR sheet */}
      <motion.div
        className="absolute bottom-0 inset-x-0 z-30 flex flex-col bg-gray-4 rounded-t-2xl shadow-[0_-8px_30px_rgba(0,0,0,0.5)]"
        style={{ height: sheetH, touchAction: "none" }}
        drag="y"
        dragConstraints={{ top: 0, bottom: maxY }}
        dragElastic={0.06}
        initial={{ y: maxY }}
        animate={{ y: expanded ? 0 : maxY }}
        transition={{ type: "spring", stiffness: 320, damping: 34 }}
        onDragEnd={(_, info) => {
          if (info.offset.y < -40 || info.velocity.y < -350) setExpanded(true);
          else if (info.offset.y > 40 || info.velocity.y > 350)
            setExpanded(false);
        }}
      >
        {/* Peek bar — handle + label (always visible) */}
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="w-full flex flex-col items-center gap-1.5 pt-2.5 pb-2.5"
        >
          <span className="h-1 w-10 rounded-full bg-white/20" />
          <span className="flex items-center gap-2 text-orange-100 text-sm font-semibold leading-130">
            <IconQrCode className="size-5" />
            {t("my_qr_code", "Mening QR kodim")}
            <ChevronUp
              className={`size-4 transition-transform duration-300 ${
                expanded ? "rotate-180" : ""
              }`}
            />
          </span>
        </button>

        {/* QR content — QR sized to the phone so it fills the sheet (no big
            empty gap); stays near the top so the peek sliver still shows. */}
        <div className="px-4 pb-safe-bottom flex flex-col items-center">
          <div
            className="relative rounded-2xl overflow-hidden mx-auto mt-4 bg-white"
            style={{ width: qrSize, height: qrSize }}
          >
            <div
              ref={qrRef}
              className="w-full h-full [&>svg]:!w-full [&>svg]:!h-full"
            />
            {!qrReady && (
              <div className="absolute inset-0 flex-center bg-white">
                <div className="size-12 border-4 border-orange-100/30 border-t-orange-100 rounded-full animate-spin" />
              </div>
            )}
          </div>

          {/* Countdown ring */}
          <div className="relative size-14 mt-3 flex-center">
            <svg
              className="absolute inset-0 w-full h-full -rotate-90"
              viewBox="0 0 36 36"
            >
              <circle
                cx="18"
                cy="18"
                r="16"
                fill="none"
                stroke="rgba(251, 199, 108, 0.18)"
                strokeWidth="2.5"
              />
              <circle
                cx="18"
                cy="18"
                r="16"
                fill="none"
                stroke={secondsLeft <= 5 ? "#FF3535" : "#FBC76C"}
                strokeWidth="2.5"
                strokeDasharray={2 * Math.PI * 16}
                strokeDashoffset={
                  2 * Math.PI * 16 * (1 - secondsLeft / QR_REFRESH_SECONDS)
                }
                strokeLinecap="round"
                style={{
                  transition: justReset
                    ? "stroke-dashoffset 0.5s ease-out, stroke 0.3s ease"
                    : "stroke-dashoffset 1s linear, stroke 0.3s ease",
                }}
              />
            </svg>
            <span
              className={`relative font-bold text-sm tabular-nums leading-none transition-colors duration-300 ${
                secondsLeft <= 5 ? "text-red" : "text-orange-100"
              }`}
            >
              {secondsLeft}
            </span>
          </div>

          <p className="text-xs text-gray-1 leading-[160%] text-center mt-3 px-2">
            {t("qr_instructions")}
          </p>
        </div>
      </motion.div>
    </div>,
    document.body
  );
}
